import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { EmbeddedPreviewRef } from '@verbit-ai/file-floater'

import { usePrevious } from 'src/hooks/usePrevious'
import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { ParagraphTextRange, TranscriptHighlight } from 'src/models/Text'
import { TranscriptParagraph } from 'src/models/Transcript'
import { TranscriptClient } from 'src/models/TranscriptClient'
import { rollbarService } from 'src/services/RollbarService'
import { locateQuoteTurns, QuoteTurnQuery, QuoteTurnResolution } from 'src/utils/quoteLocation'
import { fuzzySearch } from 'src/utils/search'

import { useFeatureFlag } from './FeatureFlagContext'
import { useTranscriptViewer, useTranscriptViewerAPI } from './TranscriptViewerProvider'
import { useTranscriptClient } from './Transcript/TranscriptContext'

export type QuoteTurn = { text: string; offset?: number }

type TranscriptHighLightValue = {
    highlight: TranscriptHighlight | null
    fileFloaterRef: React.RefObject<EmbeddedPreviewRef | null>
    selectTextToHighlight: (result: ParagraphTextRange | null) => void
    clearSearchHighlight: () => void
    /**
     * Returns the painted highlight's id, or null when the quote was not found. `reveal: 'ifHidden'`
     * scrolls only when the quote is not visible, for a click in the transcript itself.
     */
    selectTextToHighlightByOffset: (offset: number, turns?: QuoteTurn[], options?: { reveal?: 'ifHidden' }) => number | null
} | null

const Context = createContext<TranscriptHighLightValue>(null)

// A press inside an element with this attribute is not "a click in the transcript", so the highlight stays: the
// inconsistency markers decide themselves whether their click keeps, replaces or clears it.
export const KEEPS_TRANSCRIPT_HIGHLIGHT_ATTRIBUTE = 'data-keeps-transcript-highlight'

const INSIGHTS_HIGHLIGHT_SOURCE = 'insights' as const

const STATEMENT_TEXT_NOT_FOUND_FALLBACK_WARNING = '[Insights highlight] statement text not found, falling back to full paragraph'

const INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION = {
    noParagraph: 'no_paragraph',
    allTurnsLocated: 'all_turns_located',
    someTurnsLocated: 'some_turns_located',
    legacyFuzzyFilteredToOffsetParagraph: 'legacy_fuzzy_filtered_to_offset_paragraph',
    fullParagraphFallback: 'full_paragraph_fallback',
    turnsNotLocated: 'turns_not_located',
} as const

type InsightsQuoteRequest = { offset: number; turns: QuoteTurn[] }

/**
 * A mislocated quote is reported after the fact, when the only artefact is a session recording, so this
 * is emitted whether or not the user had turned debug on beforehand.
 */
const traceQuoteLocation = (details: Record<string, unknown>) => console.debug('[Insights highlight]', details)

const fullParagraphRange = (paragraph: TranscriptParagraph): ParagraphTextRange | null => {
    const text = TranscriptParagraph.textOf(paragraph)

    return text.length ? { paragraphId: paragraph.id, start: 0, end: text.length } : null
}

const asFullParagraphHighlight = (paragraph: TranscriptParagraph): ParagraphTextRange[] => {
    const range = fullParagraphRange(paragraph)

    return range ? [range] : []
}

const turnMatchResolution = (locatedCount: number, requestedCount: number) => {
    if (!locatedCount) {
        return INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.turnsNotLocated
    }
    if (locatedCount === requestedCount) {
        return INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.allTurnsLocated
    }

    return INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.someTurnsLocated
}

// Flag-off path: fuzzy search filtered to the offset paragraph.
const locateInsightsQuoteLoose = (
    transcriptClient: TranscriptClient,
    offset: number,
    turns: QuoteTurn[],
    anchorParagraph: TranscriptParagraph,
): ParagraphTextRange[] => {
    const term = turns[0]?.text.trim() ?? ''
    if (term) {
        const hit = fuzzySearch(transcriptClient, term).find((result) => result.paragraphId === anchorParagraph.id)
        if (hit) {
            traceQuoteLocation({
                featureFlagEnabled: false,
                sourceFlow: INSIGHTS_HIGHLIGHT_SOURCE,
                offset,
                paragraphId: anchorParagraph.id,
                matchResolution: INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.legacyFuzzyFilteredToOffsetParagraph,
            })

            return [hit]
        }
    }

    traceQuoteLocation({
        featureFlagEnabled: false,
        sourceFlow: INSIGHTS_HIGHLIGHT_SOURCE,
        offset,
        paragraphId: anchorParagraph.id,
        matchResolution: INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.fullParagraphFallback,
    })
    if (term) {
        rollbarService.warning(STATEMENT_TEXT_NOT_FOUND_FALLBACK_WARNING, { offset })
    }

    return asFullParagraphHighlight(anchorParagraph)
}

/**
 * The panel attaches an offset to a turn only when the backend had timing for it — turns built from
 * `highlight_spans` arrive without one — so an offset that is present is taken at face value.
 */
const asQuoteTurnQueries = (transcriptClient: TranscriptClient, turns: QuoteTurn[]): QuoteTurnQuery[] =>
    turns.map(({ text, offset }) => ({
        text,
        anchorParagraphIndex: offset === undefined ? undefined : transcriptClient.getParagraphIndexByInsightOffset(offset),
    }))

const locateInsightsQuoteStrict = (
    transcriptClient: TranscriptClient,
    offset: number,
    turns: QuoteTurn[],
    anchorParagraph: TranscriptParagraph,
    anchorParagraphIndex: number,
): ParagraphTextRange[] => {
    // No turns (pass2 off / legacy payload): today's offset-paragraph highlight.
    if (!turns.length) {
        const ranges = asFullParagraphHighlight(anchorParagraph)
        traceQuoteLocation({
            featureFlagEnabled: true,
            sourceFlow: INSIGHTS_HIGHLIGHT_SOURCE,
            offset,
            anchorParagraphId: anchorParagraph.id,
            turnCount: 0,
            rangeCount: ranges.length,
            matchResolution: INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.fullParagraphFallback,
        })

        return ranges
    }

    // Paragraph indices only: a turn's text is deposition content and must not reach the logs.
    const turnResolutions: QuoteTurnResolution[] = []

    const located = locateQuoteTurns({
        anchorParagraphIndex,
        turns: asQuoteTurnQueries(transcriptClient, turns),
        totalParagraphCount: transcriptClient.getTotalParagraphCount(),
        getParagraphByIndex: transcriptClient.getParagraphByIndex,
        onTurnResolved: (resolution) => turnResolutions.push(resolution),
    })

    // Cited turns missed — fall back to the offset paragraph so something still paints.
    const ranges = located.length ? located : asFullParagraphHighlight(anchorParagraph)
    traceQuoteLocation({
        featureFlagEnabled: true,
        sourceFlow: INSIGHTS_HIGHLIGHT_SOURCE,
        offset,
        anchorParagraphId: anchorParagraph.id,
        anchorParagraphIndex,
        turnCount: turns.length,
        rangeCount: ranges.length,
        matchResolution: located.length
            ? turnMatchResolution(located.length, turns.length)
            : INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.fullParagraphFallback,
        turnResolutions,
    })
    if (!located.length) {
        rollbarService.warning(STATEMENT_TEXT_NOT_FOUND_FALLBACK_WARNING, { offset })
    }

    return ranges
}

const locateInsightsQuote = (
    transcriptClient: TranscriptClient,
    { offset, turns }: InsightsQuoteRequest,
    strict: boolean,
): ParagraphTextRange[] => {
    const anchorParagraphIndex = transcriptClient.getParagraphIndexByInsightOffset(offset)
    const anchorParagraph = transcriptClient.getParagraphByIndex(anchorParagraphIndex)

    if (!anchorParagraph) {
        traceQuoteLocation({
            featureFlagEnabled: strict,
            sourceFlow: INSIGHTS_HIGHLIGHT_SOURCE,
            offset,
            matchResolution: INSIGHTS_HIGHLIGHT_MATCH_RESOLUTION.noParagraph,
        })
        rollbarService.warning('[Insights highlight] no paragraph resolved from offset', { offset })

        return []
    }

    return strict
        ? locateInsightsQuoteStrict(transcriptClient, offset, turns, anchorParagraph, anchorParagraphIndex)
        : locateInsightsQuoteLoose(transcriptClient, offset, turns, anchorParagraph)
}

export const TranscriptHighlightProvider = (props: PropsWithChildren) => {
    const { children } = props
    const [highlight, setHighlight] = useState<TranscriptHighlight | null>(null)
    const fileFloaterRef = useRef<EmbeddedPreviewRef | null>(null)
    // An insights quote is painted, so Search closing must leave it alone.
    const isInsightsQuoteRef = useRef(false)
    const nextHighlightIdRef = useRef(0)

    const previousHighlightId = usePrevious(highlight?.id)
    const { scrollToParagraphId } = useTranscriptViewerAPI()
    const { listScrollingContainerRef } = useTranscriptViewer()
    const transcriptClient = useTranscriptClient()
    const isOffsetStrictHighlightingEnabled = useFeatureFlag('insights_offset_strict_highlighting')

    const setResolvedHighlight = useCallback((ranges: ParagraphTextRange[], reveal: TranscriptHighlight['reveal'] = 'top') => {
        nextHighlightIdRef.current += 1
        const next = ranges.length ? { id: nextHighlightIdRef.current, ranges, reveal } : null
        setHighlight(next)

        return next?.id ?? null
    }, [])

    const selectTextToHighlight = useCallback(
        (result: ParagraphTextRange | null) => {
            isInsightsQuoteRef.current = false
            setResolvedHighlight(result ? [result] : [])
        },
        [setResolvedHighlight],
    )

    // Search resets when it closes or remounts, which must not wipe an insights quote it did not paint.
    const clearSearchHighlight = useCallback(() => {
        if (!isInsightsQuoteRef.current) {
            setHighlight(null)
        }
    }, [])

    // Located once, on the click. The next click in the transcript clears it, so only remote changes
    // (mostly ASR at the end of a live transcript) arrive while it paints, and re-locating on each of those
    // cost compute and repeated the miss warning about once a second.
    const selectTextToHighlightByOffset = useCallback(
        (offset: number, turns?: QuoteTurn[], options?: { reveal?: 'ifHidden' }) => {
            isInsightsQuoteRef.current = true

            return setResolvedHighlight(
                locateInsightsQuote(transcriptClient, { offset, turns: turns ?? [] }, isOffsetStrictHighlightingEnabled),
                options?.reveal,
            )
        },
        [isOffsetStrictHighlightingEnabled, setResolvedHighlight, transcriptClient],
    )

    // A CSS highlight paints until it is removed. Only a click in the transcript clears it, so the user can
    // compare the quote against the insights panel. Attached after the gesture that set it, so a quote
    // survives its own click.
    useEffect(() => {
        if (!highlight) {
            return
        }

        const dismiss = (event: PointerEvent) => {
            if (!listScrollingContainerRef.current?.contains(event.target as Node)) {
                return
            }
            if (event.target instanceof Element && event.target.closest(`[${KEEPS_TRANSCRIPT_HIGHLIGHT_ATTRIBUTE}]`)) {
                return
            }

            isInsightsQuoteRef.current = false
            setHighlight(null)
        }

        document.addEventListener('pointerdown', dismiss)

        return () => document.removeEventListener('pointerdown', dismiss)
    }, [highlight, listScrollingContainerRef])

    useEffect(() => {
        const isNew = highlight && highlight.id !== previousHighlightId
        const target = isNew ? highlight.ranges[0]?.paragraphId : null

        if (target) {
            scrollToParagraphId({ paragraphId: target, skipIfAlreadyRendered: true })
        }
    }, [highlight, previousHighlightId, scrollToParagraphId])

    return (
        <Context.Provider
            value={useShallowMemo({
                highlight,
                selectTextToHighlight,
                clearSearchHighlight,
                selectTextToHighlightByOffset,
                fileFloaterRef,
            })}>
            {children}
        </Context.Provider>
    )
}

export const useTranscriptHighLight = () => {
    const value = useContext(Context)

    if (value === null) {
        throw new Error('You have forgot to use <TranscriptHighlightProvider />')
    }

    return value
}
